/**
 * GuardLocationDashboard
 *
 * Enhanced dispatcher dashboard combining:
 * - DispatchStatsRow (top) - Aggregate statistics
 * - 3-column layout: Panel | Map | ActivityFeed (with integrated alerts)
 *
 * Layout:
 * ┌─────────────────────────────────────────────────────────────────────────────┐
 * │  [DispatchStatsRow - full width]                                            │
 * ├─────────────────────────────────────────────────────────────────────────────┤
 * │  ┌─ Panel (320px) ───┐ ┌─── Map (flex) ───┐ ┌─ ActivityFeed (300px) ─┐     │
 * │  │  Guard list       │ │                   │ │  Critical Alerts      │     │
 * │  │  or GroupedList   │ │                   │ │  Needs Attention      │     │
 * │  │                   │ │                   │ │  Events list          │     │
 * │  └───────────────────┘ └───────────────────┘ └───────────────────────┘     │
 * └─────────────────────────────────────────────────────────────────────────────┘
 *
 * Mobile (<600px): Tab-based layout with MobileTabBar (Map | Guards | Activity)
 * Tablet (600-1024px): 2-column with bottom drawer for activity feed
 * Desktop (>1024px): Full 3-column layout
 *
 * Alerts are now integrated into the ActivityFeed with critical alerts
 * prominently displayed at the top. Emergency SOS triggers a modal.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
  SwipeableDrawer,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlaceIcon from '@mui/icons-material/Place';
import VideocamIcon from '@mui/icons-material/Videocam';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ViewListIcon from '@mui/icons-material/ViewList';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorIcon from '@mui/icons-material/Error';
import styles from './GuardLocationDashboard.module.css';
import {
  GuardLocation,
  GuardLocationFilters,
  GuardSortOption,
  CameraLocation,
} from './types';
import GuardLocationMap from './GuardLocationMap';
import GuardLocationPanel from './GuardLocationPanel';
import DispatchStatsRow, { StatType, DispatchStats } from './DispatchStatsRow';
import ActivityFeed, { ActivityEvent, ActivityFilter } from './ActivityFeed';
import { DispatchAlert, AlertAction, AlertCoordinates } from './AlertsBar';
import GuardGroupedList, { GROUPING_THRESHOLD } from './GuardGroupedList';
import ConnectionStatus from './ConnectionStatus';
import PatrolLocationFilters from './PatrolLocationFilters';
import SiteInfoModal, { PatrolDetailData } from './SiteInfoModal';
import PatrolDetailModal from './PatrolDetailModal';
import CameraStreamModal from './CameraStreamModal';
import MobileTabBar, { MobileTab } from './MobileTabBar';
import MobileAlertBanner from './MobileAlertBanner';
import SendMessageDialog from './SendMessageDialog';
import { PatrolLocationScope } from '../../../../../api/dispatch';
import { ROUTE_SCHEDULER } from '../../../../../constants/routerPath';
import { useCameraLocations } from '../../hooks/dispatch/useCameraLocations';
import { useIncidentLocations } from '../../hooks/dispatch/useIncidentLocations';

/** LocalStorage key for camera visibility preference */
const SHOW_CAMERAS_STORAGE_KEY = 'dispatch-show-cameras';

/** LocalStorage key for guard list mode preference (Feature 4) */
const GUARD_LIST_MODE_STORAGE_KEY = 'dispatch-guard-list-mode';

// ============================================
// Types
// ============================================

export interface GuardLocationDashboardProps {
  /** Guards data to display */
  guards: GuardLocation[];
  /** Whether guard data is loading */
  isLoading?: boolean;
  /** Error message for guard data */
  error?: string | null;
  /** Last guard data update timestamp */
  lastUpdate?: Date | null;
  /** Callback to trigger guard data refresh */
  onRefresh?: () => void;
  /** Optional callback when a guard is selected */
  onGuardSelect?: (guardId: number | null, guard: GuardLocation | null) => void;
  /** Callback when guard details button is clicked (opens Guard Summary modal) */
  onGuardViewDetails?: (guard: GuardLocation) => void;
  /** Initial map center */
  initialCenter?: { lat: number; lng: number };
  /** Initial map zoom */
  initialZoom?: number;

  // Stats props
  /** Dashboard statistics */
  stats?: DispatchStats | null;
  /** Whether stats are loading */
  statsLoading?: boolean;
  /** Stats error */
  statsError?: string | null;
  /** Callback to retry loading stats */
  onStatsRetry?: () => void;

  // Activity feed props
  /** Activity events */
  activityEvents?: ActivityEvent[];
  /** Whether activity feed is loading */
  activityLoading?: boolean;
  /** Activity feed error */
  activityError?: string | null;
  /** Last activity update */
  activityLastUpdate?: Date | null;
  /** Callback to refresh activity feed */
  onActivityRefresh?: () => void;
  /** Callback when an activity item is clicked (for patrol details) */
  onActivityItemClick?: (event: ActivityEvent) => void;

  // Alerts props
  /** Dispatch alerts */
  alerts?: DispatchAlert[];
  /** Callback when an alert action is taken. Coordinates included for 'locate' action. */
  onAlertAction?: (alertId: string, action: AlertAction, alert: DispatchAlert, coordinates?: AlertCoordinates) => void;

  // Socket.IO props (Issue 5.2)
  /** Whether Socket.IO is connected for real-time updates */
  isSocketConnected?: boolean;
  /** Callback to manually reconnect socket */
  onSocketReconnect?: () => void;

  /** Callback when user clicks "Schedule Patrol" from site info modal */
  onSchedulePatrolForSite?: (addressId: number | null, siteName: string, customerName: string) => void;

  /** Callback when user clicks "Dispatch" on an incident marker (opens DispatchDialog in parent) */
  onIncidentDispatch?: (incident: { id: number }) => void;
}

const DEFAULT_FILTERS: GuardLocationFilters = {
  showActive: true,
  showStale: true,
  showOffline: false,
  showNotOnPatrol: true,
  searchQuery: '',
  customerId: null,
};

// ============================================
// Component
// ============================================

const GuardLocationDashboard: React.FC<GuardLocationDashboardProps> = ({
  guards,
  isLoading = false,
  error = null,
  lastUpdate = null,
  onRefresh,
  onGuardSelect,
  onGuardViewDetails,
  initialCenter,
  initialZoom = 12,
  // Stats
  stats = null,
  statsLoading = false,
  statsError = null,
  onStatsRetry,
  // Activity feed
  activityEvents = [],
  activityLoading = false,
  activityError = null,
  activityLastUpdate = null,
  onActivityRefresh,
  onActivityItemClick,
  // Alerts
  alerts = [],
  onAlertAction,
  // Socket.IO (Issue 5.2)
  isSocketConnected = false,
  onSocketReconnect,
  // Schedule patrol navigation
  onSchedulePatrolForSite,
  // Incident dispatch
  onIncidentDispatch,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const navigate = useNavigate();

  // State
  const [filters, setFilters] = useState<GuardLocationFilters>(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState<GuardSortOption>('name');
  const [selectedGuardId, setSelectedGuardId] = useState<number | null>(null);
  const [mapCenter, setMapCenter] = useState(
    initialCenter || (guards.length > 0 ? { lat: guards[0].latitude, lng: guards[0].longitude } : { lat: 43.6532, lng: -79.3832 })
  );
  const [mapZoom, setMapZoom] = useState(initialZoom);
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [activeStatFilter, setActiveStatFilter] = useState<StatType | null>(null);
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');
  const [mobileTab, setMobileTab] = useState<MobileTab>('map');

  // Patrol circles state
  const [showPatrolCircles, setShowPatrolCircles] = useState(true);
  const [patrolLocationStats, setPatrolLocationStats] = useState<{
    withCoordinates: number;
    missing: number;
  }>({ withCoordinates: 0, missing: 0 });

  // Patrol location filters state (Phase 2)
  const [patrolScope, setPatrolScope] = useState<PatrolLocationScope>('active');
  const [patrolDate, setPatrolDate] = useState<Date>(new Date());

  // Site info modal state
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [siteModalOpen, setSiteModalOpen] = useState(false);

  // Patrol detail modal state (opened from SiteInfoModal)
  const [patrolDetailOpen, setPatrolDetailOpen] = useState(false);
  const [selectedPatrolDetail, setSelectedPatrolDetail] = useState<PatrolDetailData | null>(null);

  // Guard filter indicator state (for showing "Showing X patrols for Guard Name")
  const [filteredGuardName, setFilteredGuardName] = useState<string | null>(null);
  const [filteredPatrolCount, setFilteredPatrolCount] = useState(0);
  const [filteredPatrolsLoading, setFilteredPatrolsLoading] = useState(false);

  // Camera visibility state (persisted to localStorage)
  const [showCameras, setShowCameras] = useState(() => {
    const stored = localStorage.getItem(SHOW_CAMERAS_STORAGE_KEY);
    return stored !== null ? stored === 'true' : true; // Default: ON
  });

  // Camera stream modal state
  const [cameraStreamModalOpen, setCameraStreamModalOpen] = useState(false);
  const [selectedStreamCamera, setSelectedStreamCamera] = useState<CameraLocation | null>(null);

  // Fetch camera locations
  const { cameras, isLoading: camerasLoading } = useCameraLocations({
    enabled: showCameras,
    refetchInterval: 60000, // 60 seconds - cameras don't move
  });

  // Fetch active VMS incidents for map markers
  const {
    incidents: mapIncidents,
    removeIncident: removeMapIncident,
  } = useIncidentLocations({ enabled: true });

  // Feature 4: User-toggleable guard grouping
  const [guardListMode, setGuardListMode] = useState<'flat' | 'grouped'>(() => {
    const stored = localStorage.getItem(GUARD_LIST_MODE_STORAGE_KEY);
    if (stored === 'flat' || stored === 'grouped') return stored;
    return guards.length >= GROUPING_THRESHOLD ? 'grouped' : 'flat';
  });
  const useGroupedList = guardListMode === 'grouped';

  // Update default when guard count crosses threshold (only if user hasn't set preference)
  useEffect(() => {
    const stored = localStorage.getItem(GUARD_LIST_MODE_STORAGE_KEY);
    if (!stored) {
      setGuardListMode(guards.length >= GROUPING_THRESHOLD ? 'grouped' : 'flat');
    }
  }, [guards.length]);

  const handleToggleGuardListMode = useCallback(() => {
    setGuardListMode((prev) => {
      const next = prev === 'flat' ? 'grouped' : 'flat';
      localStorage.setItem(GUARD_LIST_MODE_STORAGE_KEY, next);
      return next;
    });
  }, []);

  // Feature 1: Tablet activity feed drawer state
  const [isTabletDrawerOpen, setIsTabletDrawerOpen] = useState(false);
  const showTabletDrawer = isTablet && !isMobile;

  // Ensure panel is open when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setIsPanelOpen(true);
    }
  }, [isMobile]);

  // Update map center when guards first load
  useEffect(() => {
    if (!initialCenter && guards.length > 0 && mapCenter.lat === 43.6532) {
      setMapCenter({ lat: guards[0].latitude, lng: guards[0].longitude });
    }
  }, [guards, initialCenter, mapCenter.lat]);

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters: Partial<GuardLocationFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Handle guard selection
  const handleGuardSelect = useCallback(
    (guardId: number) => {
      setSelectedGuardId(guardId);
      const guard = guards.find((g) => g.guardId === guardId) || null;
      onGuardSelect?.(guardId, guard);
    },
    [guards, onGuardSelect]
  );

  // Handle view details (center map on guard) - used for map marker clicks
  const handleViewDetails = useCallback(
    (guardId: number) => {
      const guard = guards.find((g) => g.guardId === guardId);
      if (guard) {
        setMapCenter({ lat: guard.latitude, lng: guard.longitude });
        setMapZoom(15);
        setSelectedGuardId(guardId);
        onGuardSelect?.(guardId, guard);

        // Switch to map tab on mobile
        if (isMobile) {
          setMobileTab('map');
        }
      }
    },
    [guards, onGuardSelect, isMobile]
  );

  // Send message dialog state
  const [messageGuard, setMessageGuard] = useState<GuardLocation | null>(null);

  const handleGuardSendMessage = useCallback(
    (guard: GuardLocation) => {
      setMessageGuard(guard);
    },
    []
  );

  // Handle guard details button click (opens Guard Summary modal)
  const handleGuardViewDetails = useCallback(
    (guard: GuardLocation) => {
      onGuardViewDetails?.(guard);
    },
    [onGuardViewDetails]
  );

  // Handle "View Details" on map marker (opens Guard Summary modal)
  const handleMapViewDetails = useCallback(
    (guardId: number) => {
      const guard = guards.find((g) => g.guardId === guardId);
      if (guard) {
        onGuardViewDetails?.(guard);
      }
    },
    [guards, onGuardViewDetails]
  );

  // Handle map changes
  const handleMapChange = useCallback((center: { lat: number; lng: number }, zoom: number) => {
    setMapCenter(center);
    setMapZoom(zoom);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  // Toggle panel
  const togglePanel = useCallback(() => {
    setIsPanelOpen((prev) => !prev);
  }, []);

  // Handle stat card click - toggle filter
  const handleStatClick = useCallback((statType: StatType) => {
    // Toggle: clicking same stat deselects, clicking different stat selects it
    setActiveStatFilter((current) => (current === statType ? null : statType));
  }, []);

  // Feature 3: Sync filters when activeStatFilter changes — consistent across panels
  useEffect(() => {
    if (activeStatFilter === null) {
      // No stat selected - reset to defaults
      setFilters(DEFAULT_FILTERS);
      setActivityFilter('all');
    } else if (activeStatFilter === 'onDuty') {
      // On Duty: Show all on-duty guards AND filter activity to on-duty guards only
      setFilters({
        ...DEFAULT_FILTERS,
        showActive: true,
        showStale: true,
        showOffline: false,
        showNotOnPatrol: true,
      });
      setActivityFilter('all');
    } else if (activeStatFilter === 'patrolling') {
      // Patrolling: Filter guards to patrolling AND filter activity to patrol events
      setFilters({
        ...DEFAULT_FILTERS,
        showActive: true,
        showStale: true,
        showOffline: false,
        showNotOnPatrol: false,
      });
      setActivityFilter('all');
    } else if (activeStatFilter === 'alerts') {
      // Alerts: Switch activity feed to alerts AND highlight guards with alerts
      setActivityFilter('alerts');
      setFilters(DEFAULT_FILTERS);
    }
    // 'completed' doesn't change filters
  }, [activeStatFilter]);

  // Feature 3: Compute which guards have active alerts (for panel highlighting)
  const guardsWithAlerts = useMemo(() => {
    const activeAlerts = alerts.filter((a) => !a.dismissedAt);
    return new Set(activeAlerts.map((a) => a.guardId));
  }, [alerts]);

  // Feature 3: Clear active stat filter
  const handleClearStatFilter = useCallback(() => {
    setActiveStatFilter(null);
  }, []);

  // Handle alert action
  const handleAlertAction = useCallback(
    (alertId: string, action: AlertAction, alert: DispatchAlert, coordinates?: AlertCoordinates) => {
      if (action === 'view') {
        // Center map on the guard's current location
        handleViewDetails(alert.guardId);
        // handleViewDetails already switches to map tab on mobile
      } else if (action === 'locate' && coordinates) {
        // Center map on specific coordinates (e.g., emergency SOS location)
        setMapCenter({ lat: coordinates.latitude, lng: coordinates.longitude });
        setMapZoom(17); // Zoom in closer for precise location
        if (isMobile) {
          setMobileTab('map');
        }
      }
      // Pass to parent handler for API calls
      onAlertAction?.(alertId, action, alert, coordinates);
    },
    [handleViewDetails, onAlertAction, isMobile]
  );

  // Handle activity event click - center map on guard
  const handleActivityEventClick = useCallback(
    (guardId: number) => {
      handleViewDetails(guardId);
    },
    [handleViewDetails]
  );

  // Handle patrol circles toggle
  const handlePatrolCirclesToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setShowPatrolCircles(event.target.checked);
  }, []);

  // Handle patrol locations stats update from map
  const handlePatrolLocationsChange = useCallback(
    (stats: { withCoordinates: number; missing: number }) => {
      setPatrolLocationStats(stats);
    },
    []
  );

  // Handle patrol view details (open patrol modal)
  const handlePatrolViewDetails = useCallback(
    (patrolId: number) => {
      // Find the guard associated with this patrol and open their details
      const guard = guards.find(
        (g) => g.currentPatrol?.patrolId === patrolId
      );
      if (guard) {
        onGuardViewDetails?.(guard);
      }
    },
    [guards, onGuardViewDetails]
  );

  // Handle patrol scope filter change (Phase 2)
  const handlePatrolScopeChange = useCallback((scope: PatrolLocationScope) => {
    setPatrolScope(scope);
  }, []);

  // Handle patrol date filter change (Phase 2)
  const handlePatrolDateChange = useCallback((date: Date) => {
    setPatrolDate(date);
  }, []);

  // Handle view site details (open site info modal)
  const handleViewSiteDetails = useCallback((addressId: number) => {
    setSelectedSiteId(addressId);
    setSiteModalOpen(true);
  }, []);

  // Handle site info modal close
  const handleCloseSiteModal = useCallback(() => {
    setSiteModalOpen(false);
    setSelectedSiteId(null);
  }, []);

  // Handle view patrol details from SiteInfoModal
  const handleViewPatrolFromSite = useCallback((data: PatrolDetailData) => {
    setSelectedPatrolDetail(data);
    setPatrolDetailOpen(true);
  }, []);

  // Handle patrol detail modal close
  const handleClosePatrolDetail = useCallback(() => {
    setPatrolDetailOpen(false);
    setSelectedPatrolDetail(null);
  }, []);

  // Handle guard filter state change (from GuardLocationMap)
  const handleGuardFilterChange = useCallback((guardName: string | null, patrolCount: number, isLoading?: boolean) => {
    setFilteredGuardName(guardName);
    setFilteredPatrolCount(patrolCount);
    setFilteredPatrolsLoading(isLoading ?? false);
  }, []);

  // Handle clearing the guard filter
  const handleClearGuardFilter = useCallback(() => {
    setSelectedGuardId(null);
    setFilteredGuardName(null);
    setFilteredPatrolCount(0);
    setFilteredPatrolsLoading(false);
    onGuardSelect?.(null, null);
  }, [onGuardSelect]);

  // Cross-tab navigation: focus guard on map and switch to map tab on mobile
  const focusGuardOnMap = useCallback(
    (guardId: number) => {
      const guard = guards.find((g) => g.guardId === guardId);
      if (guard) {
        setMapCenter({ lat: guard.latitude, lng: guard.longitude });
        setMapZoom(16);
        setSelectedGuardId(guardId);
        onGuardSelect?.(guardId, guard);
        if (isMobile) {
          setMobileTab('map');
        }
      }
    },
    [guards, onGuardSelect, isMobile]
  );

  // Handle cameras toggle
  const handleCamerasToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setShowCameras(newValue);
    localStorage.setItem(SHOW_CAMERAS_STORAGE_KEY, String(newValue));
  }, []);

  // Handle camera view stream action
  const handleCameraViewStream = useCallback((camera: CameraLocation) => {
    setSelectedStreamCamera(camera);
    setCameraStreamModalOpen(true);
  }, []);

  // Handle camera view alerts action — navigate to CANVMS incidents filtered by camera
  const handleCameraViewAlerts = useCallback((camera: CameraLocation) => {
    navigate(`/canvms?tab=incidents&cameraId=${camera.id}`);
  }, [navigate]);

  // Handle camera dispatch — navigate to scheduler with customer/address pre-filled
  const handleCameraDispatch = useCallback((camera: CameraLocation) => {
    const params = new URLSearchParams();
    params.set('action', 'newPatrol');
    if (camera.customer_name) params.set('customerName', camera.customer_name);
    if (camera.address_title) params.set('addressName', camera.address_title);
    navigate(`${ROUTE_SCHEDULER}?${params.toString()}`);
  }, [navigate]);

  // Handle incident view details — navigate to CANVMS incident detail
  const handleIncidentViewDetails = useCallback((incident: { id: number }) => {
    navigate(`/canvms?tab=incidents&incidentId=${incident.id}`);
  }, [navigate]);

  // Handle incident dispatch — navigate to scheduler with customer/address from camera data
  const handleIncidentDispatch = useCallback((incident: { id: number }) => {
    // Find full incident data to get camera info
    const fullIncident = mapIncidents.find(i => i.id === incident.id);
    // Find matching camera for customer/address data
    const camera = cameras.find(c =>
      (fullIncident?.camera_display_name && c.display_name === fullIncident.camera_display_name) ||
      (fullIncident?.camera_name && c.name === fullIncident.camera_name)
    );
    const params = new URLSearchParams();
    params.set('action', 'newPatrol');
    if (camera?.customer_name) params.set('customerName', camera.customer_name);
    if (camera?.address_title) params.set('addressName', camera.address_title);
    navigate(`${ROUTE_SCHEDULER}?${params.toString()}`);
  }, [mapIncidents, cameras, navigate]);

  // Handle incident quick resolve — remove from map
  const handleIncidentResolved = useCallback((incidentId: number) => {
    removeMapIncident(incidentId);
  }, [removeMapIncident]);

  // Handle camera stream modal close
  const handleCloseCameraStreamModal = useCallback(() => {
    setCameraStreamModalOpen(false);
    setSelectedStreamCamera(null);
  }, []);

  // Feature 5: Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip when typing in inputs or when a modal is open
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (siteModalOpen || patrolDetailOpen || cameraStreamModalOpen) return;

      switch (e.code) {
        case 'KeyR':
          e.preventDefault();
          onRefresh?.();
          break;
        case 'Escape':
          e.preventDefault();
          setSelectedGuardId(null);
          setActiveStatFilter(null);
          onGuardSelect?.(null, null);
          break;
        case 'KeyA':
          e.preventDefault();
          setActivityFilter((prev) => (prev === 'alerts' ? 'all' : 'alerts'));
          break;
        case 'Digit1':
          e.preventDefault();
          setActiveStatFilter((c) => (c === 'onDuty' ? null : 'onDuty'));
          break;
        case 'Digit2':
          e.preventDefault();
          setActiveStatFilter((c) => (c === 'patrolling' ? null : 'patrolling'));
          break;
        case 'Digit3':
          // No-op (completed)
          break;
        case 'Digit4':
          e.preventDefault();
          setActiveStatFilter((c) => (c === 'alerts' ? null : 'alerts'));
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onRefresh, onGuardSelect, siteModalOpen, patrolDetailOpen, cameraStreamModalOpen]);

  // Feature 1: Critical alerts for tablet banner overlay
  const criticalAlertsForBanner = useMemo(
    () => alerts.filter((a) => !a.dismissedAt && a.severity === 'critical'),
    [alerts]
  );

  // Feature 1: Summary counts for tablet bar
  const tabletSummaryCounts = useMemo(() => {
    const activeAlertCount = alerts.filter((a) => !a.dismissedAt).length;
    return { alerts: activeAlertCount, events: activityEvents.length };
  }, [alerts, activityEvents]);

  // Active alert count for mobile tab bar badge
  const activeAlertCount = useMemo(
    () => alerts.filter((a) => !a.dismissedAt).length,
    [alerts]
  );

  // Filter guards for map (apply filters)
  const filteredGuards = useMemo(() => {
    return guards.filter((guard) => {
      switch (guard.status) {
        case 'active':
          if (!filters.showActive) return false;
          break;
        case 'stale':
          if (!filters.showStale) return false;
          break;
        case 'offline':
          if (!filters.showOffline) return false;
          break;
        case 'not_on_patrol':
          if (!filters.showNotOnPatrol) return false;
          break;
      }

      // Apply stat filter
      if (activeStatFilter === 'patrolling') {
        if (!guard.currentPatrol || guard.currentPatrol.patrolStatus !== 'in_progress') {
          return false;
        }
      }

      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const nameMatch = guard.name?.toLowerCase().includes(query);
        const customerMatch = guard.currentPatrol?.customerName?.toLowerCase().includes(query);
        const siteMatch = guard.currentPatrol?.siteName?.toLowerCase().includes(query);
        if (!nameMatch && !customerMatch && !siteMatch) return false;
      }

      return true;
    });
  }, [guards, filters, activeStatFilter]);

  // Feature 3: Filter activity events based on stat filter
  const filteredActivityEvents = useMemo(() => {
    if (activeStatFilter === 'patrolling') {
      // Only show patrol-related events
      return activityEvents.filter((e) =>
        e.type === 'patrol_started' || e.type === 'patrol_completed' || e.type === 'checkpoint_completed'
      );
    }
    if (activeStatFilter === 'onDuty') {
      // Only show events for on-duty (filtered) guards
      const onDutyIds = new Set(filteredGuards.map((g) => g.guardId));
      return activityEvents.filter((e) => onDutyIds.has(e.guardId));
    }
    return activityEvents;
  }, [activityEvents, activeStatFilter, filteredGuards]);

  // Show activity feed inline on desktop only; tablet uses drawer
  const showActivityFeed = !isTablet;

  // Feature 3: Stat filter label for chip display
  const statFilterLabels: Record<StatType, string> = {
    onDuty: 'On Duty',
    patrolling: 'Patrolling',
    completed: 'Completed',
    alerts: 'Alerts',
  };

  return (
    <Box className={styles.dashboard}>
      {isMobile ? (
        /* ====== MOBILE: Tab-based layout ====== */
        <>
          {/* Persistent alert banner */}
          <MobileAlertBanner
            alerts={alerts}
            onView={() => setMobileTab('activity')}
            collapsed={mobileTab === 'activity'}
          />

          {/* Compact stats strip */}
          <Box className={styles.statsHeader}>
            <DispatchStatsRow
              stats={stats}
              isLoading={statsLoading}
              error={statsError}
              onStatClick={handleStatClick}
              activeFilter={activeStatFilter}
              onRetry={onStatsRetry}
              compact
            />
          </Box>

          {/* Active filter chip */}
          {activeStatFilter && (
            <Box className={styles.activeFilterChipRow}>
              <Chip
                label={`Filtered: ${statFilterLabels[activeStatFilter]}`}
                onDelete={handleClearStatFilter}
                color="primary"
                size="small"
                variant="outlined"
              />
            </Box>
          )}

          {/* Tab content */}
          <Box className={styles.mobileTabContent}>
            {mobileTab === 'map' && (
              <>
                {/* Floating filter chips on map */}
                <Box className={styles.mobileMapFilters}>
                  <Chip
                    icon={<PlaceIcon style={{ fontSize: 16 }} />}
                    label={`Patrols${patrolLocationStats.withCoordinates > 0 ? ` (${patrolLocationStats.withCoordinates})` : ''}`}
                    onClick={() => setShowPatrolCircles((p) => !p)}
                    color={showPatrolCircles ? 'primary' : 'default'}
                    variant={showPatrolCircles ? 'filled' : 'outlined'}
                    size="small"
                  />
                  <Chip
                    icon={<VideocamIcon style={{ fontSize: 16 }} />}
                    label={`Cameras${showCameras && cameras.length > 0 ? ` (${cameras.length})` : ''}`}
                    onClick={() => {
                      const newValue = !showCameras;
                      setShowCameras(newValue);
                      localStorage.setItem(SHOW_CAMERAS_STORAGE_KEY, String(newValue));
                    }}
                    color={showCameras ? 'primary' : 'default'}
                    variant={showCameras ? 'filled' : 'outlined'}
                    size="small"
                  />
                </Box>

                {/* Guard filter indicator */}
                {filteredGuardName && (
                  <Chip
                    label={
                      filteredPatrolsLoading
                        ? `Loading patrols for ${filteredGuardName}...`
                        : `Showing ${filteredPatrolCount} patrol${filteredPatrolCount !== 1 ? 's' : ''} for ${filteredGuardName}`
                    }
                    onDelete={handleClearGuardFilter}
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 44,
                      left: 12,
                      zIndex: 1,
                      boxShadow: 2,
                    }}
                  />
                )}

                <GuardLocationMap
                  guards={filteredGuards}
                  selectedGuardId={selectedGuardId}
                  onGuardSelect={handleGuardSelect}
                  onGuardViewDetails={handleMapViewDetails}
                  onPatrolViewDetails={handlePatrolViewDetails}
                  onViewSiteDetails={handleViewSiteDetails}
                  center={mapCenter}
                  zoom={mapZoom}
                  onMapChange={handleMapChange}
                  isLoading={isLoading}
                  error={error}
                  showPatrolCircles={showPatrolCircles}
                  onPatrolLocationsChange={handlePatrolLocationsChange}
                  patrolScope={patrolScope}
                  patrolDate={patrolDate}
                  onGuardFilterChange={handleGuardFilterChange}
                  cameras={cameras}
                  showCameras={showCameras}
                  onCameraViewStream={handleCameraViewStream}
                  onCameraViewAlerts={handleCameraViewAlerts}
                  onCameraDispatch={handleCameraDispatch}
                  incidents={mapIncidents}
                  onIncidentViewDetails={handleIncidentViewDetails}
                  onIncidentDispatch={handleIncidentDispatch}
                  onIncidentResolved={handleIncidentResolved}
                />
              </>
            )}

            {mobileTab === 'guards' && (
              <Box className={styles.mobileGuardsPanel}>
                <Box className={styles.panelModeToggle}>
                  <Tooltip title={useGroupedList ? 'Switch to flat list' : 'Switch to grouped list'}>
                    <IconButton size="small" onClick={handleToggleGuardListMode}>
                      {useGroupedList ? <ViewListIcon fontSize="small" /> : <GroupWorkIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
                {useGroupedList ? (
                  <Box className={styles.groupedListWrapper}>
                    <GuardGroupedList
                      guards={guards}
                      searchQuery={filters.searchQuery}
                      selectedGuardId={selectedGuardId}
                      onGuardSelect={handleViewDetails}
                      showProgress
                    />
                  </Box>
                ) : (
                  <GuardLocationPanel
                    guards={guards}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                    selectedGuardId={selectedGuardId}
                    onGuardSelect={handleViewDetails}
                    onGuardViewDetails={handleGuardViewDetails}
                    onGuardSendMessage={handleGuardSendMessage}
                    lastUpdate={lastUpdate}
                    onRefresh={handleRefresh}
                    isLoading={isLoading}
                    onSiteClick={handleViewSiteDetails}
                  />
                )}
              </Box>
            )}

            {mobileTab === 'activity' && (
              <ActivityFeed
                events={filteredActivityEvents}
                isLoading={activityLoading}
                error={activityError}
                filter={activityFilter}
                onFilterChange={setActivityFilter}
                onRefresh={onActivityRefresh}
                onItemClick={onActivityItemClick}
                lastUpdate={activityLastUpdate}
                maxHeight="100%"
                dispatchAlerts={alerts}
                onAlertAction={handleAlertAction}
                onSiteClick={handleViewSiteDetails}
              />
            )}
          </Box>

          {/* Bottom tab bar */}
          <MobileTabBar
            activeTab={mobileTab}
            onTabChange={setMobileTab}
            alertCount={activeAlertCount}
          />
        </>
      ) : (
        /* ====== DESKTOP/TABLET: Original 3-column layout ====== */
        <>
          {/* Stats Row with Connection Status */}
          <Box className={styles.statsHeader}>
            <DispatchStatsRow
              stats={stats}
              isLoading={statsLoading}
              error={statsError}
              onStatClick={handleStatClick}
              activeFilter={activeStatFilter}
              onRetry={onStatsRetry}
            />
            {/* Connection Status Indicator */}
            <Box className={styles.connectionStatusWrapper}>
              <ConnectionStatus
                isConnected={isSocketConnected}
                onReconnect={onSocketReconnect}
              />
              {/* Keyboard shortcuts help */}
              <Tooltip
                title={
                  <Box className={styles.shortcutsList}>
                    <Box className={styles.shortcutItem}><Box component="span" className={styles.shortcutKey}>R</Box> Refresh</Box>
                    <Box className={styles.shortcutItem}><Box component="span" className={styles.shortcutKey}>Esc</Box> Deselect / Clear filter</Box>
                    <Box className={styles.shortcutItem}><Box component="span" className={styles.shortcutKey}>A</Box> Toggle alerts filter</Box>
                    <Box className={styles.shortcutItem}><Box component="span" className={styles.shortcutKey}>1</Box> On Duty</Box>
                    <Box className={styles.shortcutItem}><Box component="span" className={styles.shortcutKey}>2</Box> Patrolling</Box>
                    <Box className={styles.shortcutItem}><Box component="span" className={styles.shortcutKey}>4</Box> Alerts</Box>
                  </Box>
                }
                placement="bottom-end"
              >
                <IconButton size="small" className={styles.shortcutsHelp}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Active filter chip */}
          {activeStatFilter && (
            <Box className={styles.activeFilterChipRow}>
              <Chip
                label={`Filtered: ${statFilterLabels[activeStatFilter]}`}
                onDelete={handleClearStatFilter}
                color="primary"
                size="small"
                variant="outlined"
              />
            </Box>
          )}

          {/* Patrol Location Filters Toolbar */}
          <Box className={styles.filtersToolbar}>
            <Tooltip title="Show/hide 50m patrol area circles on the map">
              <FormControlLabel
                control={
                  <Switch
                    checked={showPatrolCircles}
                    onChange={handlePatrolCirclesToggle}
                    size="small"
                    color="primary"
                  />
                }
                label={
                  <Box className={styles.toggleLabel}>
                    <PlaceIcon fontSize="small" />
                    <Typography variant="body2">Patrol Areas</Typography>
                    {patrolLocationStats.withCoordinates > 0 && (
                      <Typography variant="caption" color="textSecondary">
                        ({patrolLocationStats.withCoordinates}
                        {patrolLocationStats.missing > 0 && `/${patrolLocationStats.withCoordinates + patrolLocationStats.missing}`})
                      </Typography>
                    )}
                  </Box>
                }
                labelPlacement="end"
              />
            </Tooltip>
            {showPatrolCircles && (
              <PatrolLocationFilters
                scope={patrolScope}
                date={patrolDate}
                onScopeChange={handlePatrolScopeChange}
                onDateChange={handlePatrolDateChange}
              />
            )}

            {/* Camera visibility toggle */}
            <Tooltip title="Show/hide VMS cameras on the map">
              <FormControlLabel
                control={
                  <Switch
                    checked={showCameras}
                    onChange={handleCamerasToggle}
                    size="small"
                    color="primary"
                  />
                }
                label={
                  <Box className={styles.toggleLabel}>
                    <VideocamIcon fontSize="small" />
                    <Typography variant="body2">Cameras</Typography>
                    {showCameras && cameras.length > 0 && (
                      <Typography variant="caption" color="textSecondary">
                        ({cameras.length})
                      </Typography>
                    )}
                  </Box>
                }
                labelPlacement="end"
                sx={{ ml: 2 }}
              />
            </Tooltip>
          </Box>

          {/* Main Content — 3-column layout */}
          <Box className={styles.mainContent}>
            {/* Left Panel */}
            <Box className={styles.panelContainer}>
              <Box className={styles.panelModeToggle}>
                <Tooltip title={useGroupedList ? 'Switch to flat list' : 'Switch to grouped list'}>
                  <IconButton size="small" onClick={handleToggleGuardListMode}>
                    {useGroupedList ? <ViewListIcon fontSize="small" /> : <GroupWorkIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
              {useGroupedList ? (
                <Box className={styles.groupedListWrapper}>
                  <GuardGroupedList
                    guards={guards}
                    searchQuery={filters.searchQuery}
                    selectedGuardId={selectedGuardId}
                    onGuardSelect={handleViewDetails}
                    showProgress
                  />
                </Box>
              ) : (
                <GuardLocationPanel
                  guards={guards}
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  selectedGuardId={selectedGuardId}
                  onGuardSelect={handleViewDetails}
                  onGuardViewDetails={handleGuardViewDetails}
                  onGuardSendMessage={handleGuardSendMessage}
                  lastUpdate={lastUpdate}
                  onRefresh={handleRefresh}
                  isLoading={isLoading}
                  onSiteClick={handleViewSiteDetails}
                />
              )}
            </Box>

            {/* Map Container */}
            <Box className={styles.mapContainer} sx={{ position: 'relative' }}>
              {/* Critical alerts floating banner — tablet only */}
              {showTabletDrawer && criticalAlertsForBanner.length > 0 && (
                <Box className={styles.criticalAlertsBanner}>
                  {criticalAlertsForBanner.map((alert) => (
                    <Box
                      key={alert.alertId}
                      className={styles.criticalAlertBannerItem}
                      onClick={() => handleAlertAction(alert.alertId, 'view', alert)}
                    >
                      <ErrorIcon fontSize="small" />
                      <span>{alert.guardName} — {alert.message || alert.type}</span>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Guard filter indicator */}
              {filteredGuardName && (
                <Chip
                  label={
                    filteredPatrolsLoading
                      ? `Loading patrols for ${filteredGuardName}...`
                      : `Showing ${filteredPatrolCount} patrol${filteredPatrolCount !== 1 ? 's' : ''} for ${filteredGuardName}`
                  }
                  onDelete={handleClearGuardFilter}
                  color="primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: showTabletDrawer && criticalAlertsForBanner.length > 0 ? 60 : 12,
                    left: 12,
                    zIndex: 1,
                    boxShadow: 2,
                  }}
                />
              )}
              <GuardLocationMap
                guards={filteredGuards}
                selectedGuardId={selectedGuardId}
                onGuardSelect={handleGuardSelect}
                onGuardViewDetails={handleMapViewDetails}
                onPatrolViewDetails={handlePatrolViewDetails}
                onViewSiteDetails={handleViewSiteDetails}
                center={mapCenter}
                zoom={mapZoom}
                onMapChange={handleMapChange}
                isLoading={isLoading}
                error={error}
                showPatrolCircles={showPatrolCircles}
                onPatrolLocationsChange={handlePatrolLocationsChange}
                patrolScope={patrolScope}
                patrolDate={patrolDate}
                onGuardFilterChange={handleGuardFilterChange}
                cameras={cameras}
                showCameras={showCameras}
                onCameraViewStream={handleCameraViewStream}
                onCameraViewAlerts={handleCameraViewAlerts}
                onCameraDispatch={handleCameraDispatch}
                incidents={mapIncidents}
                onIncidentViewDetails={handleIncidentViewDetails}
                onIncidentDispatch={handleIncidentDispatch}
                onIncidentResolved={handleIncidentResolved}
              />

              {/* Tablet floating summary bar */}
              {showTabletDrawer && (
                <Box
                  className={styles.tabletSummaryBar}
                  onClick={() => setIsTabletDrawerOpen(true)}
                  role="button"
                  tabIndex={0}
                >
                  <Typography className={styles.tabletSummaryBarText}>
                    {tabletSummaryCounts.alerts > 0 && (
                      <Box component="span" className={styles.tabletSummaryBarAlert}>
                        {tabletSummaryCounts.alerts} alert{tabletSummaryCounts.alerts !== 1 ? 's' : ''}
                      </Box>
                    )}
                    {tabletSummaryCounts.alerts > 0 && tabletSummaryCounts.events > 0 && ' · '}
                    {tabletSummaryCounts.events > 0 && (
                      <>{tabletSummaryCounts.events} event{tabletSummaryCounts.events !== 1 ? 's' : ''}</>
                    )}
                    {tabletSummaryCounts.alerts === 0 && tabletSummaryCounts.events === 0 && 'Activity Feed'}
                  </Typography>
                  <KeyboardArrowUpIcon fontSize="small" />
                </Box>
              )}
            </Box>

            {/* Activity Feed (desktop only) */}
            {showActivityFeed && (
              <Box className={styles.activityFeedContainer}>
                <ActivityFeed
                  events={filteredActivityEvents}
                  isLoading={activityLoading}
                  error={activityError}
                  filter={activityFilter}
                  onFilterChange={setActivityFilter}
                  onRefresh={onActivityRefresh}
                  onItemClick={onActivityItemClick}
                  lastUpdate={activityLastUpdate}
                  maxHeight="100%"
                  dispatchAlerts={alerts}
                  onAlertAction={handleAlertAction}
                  onSiteClick={handleViewSiteDetails}
                />
              </Box>
            )}
          </Box>

          {/* Tablet activity feed drawer */}
          {showTabletDrawer && (
            <SwipeableDrawer
              anchor="bottom"
              open={isTabletDrawerOpen}
              onClose={() => setIsTabletDrawerOpen(false)}
              onOpen={() => setIsTabletDrawerOpen(true)}
              disableSwipeToOpen
              ModalProps={{ keepMounted: true }}
              PaperProps={{
                sx: {
                  height: '70vh',
                  overflow: 'visible',
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16,
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 40,
                  height: 6,
                  backgroundColor: 'grey.400',
                  borderRadius: 3,
                }}
              />
              <Box className={styles.tabletDrawerContent}>
                <ActivityFeed
                  events={filteredActivityEvents}
                  isLoading={activityLoading}
                  error={activityError}
                  filter={activityFilter}
                  onFilterChange={setActivityFilter}
                  onRefresh={onActivityRefresh}
                  onItemClick={onActivityItemClick}
                  lastUpdate={activityLastUpdate}
                  maxHeight="100%"
                  dispatchAlerts={alerts}
                  onAlertAction={handleAlertAction}
                  onSiteClick={handleViewSiteDetails}
                />
              </Box>
            </SwipeableDrawer>
          )}
        </>
      )}

      {/* Modals — always rendered regardless of layout */}
      <SiteInfoModal
        open={siteModalOpen}
        onClose={handleCloseSiteModal}
        addressId={selectedSiteId}
        onViewPatrolDetails={handleViewPatrolFromSite}
        onSchedulePatrol={onSchedulePatrolForSite}
      />

      {selectedPatrolDetail && (
        <PatrolDetailModal
          open={patrolDetailOpen}
          onClose={handleClosePatrolDetail}
          scheduleAssignmentId={selectedPatrolDetail.scheduleAssignmentId}
          patrolId={selectedPatrolDetail.scheduleAssignmentId}
          guardName={selectedPatrolDetail.guardName}
          siteName={selectedPatrolDetail.siteName}
          patrolStatus={selectedPatrolDetail.patrolStatus}
          startedAt={selectedPatrolDetail.startedAt}
        />
      )}

      <CameraStreamModal
        open={cameraStreamModalOpen}
        camera={selectedStreamCamera}
        onClose={handleCloseCameraStreamModal}
      />

      {messageGuard && (
        <SendMessageDialog
          open={!!messageGuard}
          onClose={() => setMessageGuard(null)}
          guardId={messageGuard.guardId}
          guardName={messageGuard.name}
        />
      )}
    </Box>
  );
};

export default GuardLocationDashboard;
